import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AppConfig } from 'app/config/service.config';
import { User, UsersPagination } from 'app/core/user/user.types';
import {
    BehaviorSubject,
    catchError,
    map,
    Observable,
    of,
    ReplaySubject,
    switchMap,
    take,
    tap,
    throwError,
} from 'rxjs';
import { AuthService } from '../auth/auth.service';
import { LogService } from '../logging/log.service';
import { JwtService } from '../jwt/jwt.service';
import { environment } from 'environments/environment';
import { BrandService } from '../brand/brand.service';

export interface ApiResponse<T> {
    timestamp: string;
    status: number;
    message: string;
    data: T;
}

@Injectable({ providedIn: 'root' })
export class UserService {
    private _user: ReplaySubject<User> = new ReplaySubject<User>(1);

    private _users: BehaviorSubject<User[] | null> = new BehaviorSubject(null);
    private _usersPagination: BehaviorSubject<UsersPagination | null> =
        new BehaviorSubject(null);

    /**
     * Constructor
     */
    constructor(
        private _httpClient: HttpClient,
        private _apiServer: AppConfig,
        private _authService: AuthService,
        private _jwtService: JwtService,
        private _logging: LogService,
        private _brandService: BrandService
    ) { }

    private getServiceEndpoint(): string {
        return this._brandService.getApiEndpoint(this._apiServer);
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Setter & getter for user
     *
     * @param value
     */
    set user(value: User) {
        // Store the value
        this._user.next(value);
    }

    get user$(): Observable<User> {
        return this._user.asObservable();
    }

    /** Setter for users */
    set users(value: User[]) {
        this._users.next(value);
    }
    /** Getter for users */
    get users$(): Observable<User[]> {
        return this._users.asObservable();
    }
    /** Setter for users pagination */
    get usersPagination$(): Observable<UsersPagination> {
        return this._usersPagination.asObservable();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Get the current logged in user data
     */
    getUser(): Observable<any> {
        if (!this._authService.accessToken) {
            console.warn('User Not Logged in');
            return of(false);
        }

        let userService = this.getServiceEndpoint();

        // let ownerId = this._jwtService.getJwtPayload(this._authService.accessToken).jti
        const header = {
            headers: new HttpHeaders().set(
                'Authorization',
                this._authService.accessToken
            ),
        };

        return this._httpClient
            .get<any>(userService + '/users/details', header)
            .pipe(
                catchError(() =>
                    // Return false
                    of(false)
                ),
                switchMap(async (response: any) => {
                    if (response) {
                        // this._logging.debug(
                        //     'Response from UserService (Get)',
                        //     response
                        // );
                        this._user.next(response.data);
                        return response.data;
                    } else return response;
                })
            );
    }

    /**
     * Update the user
     *
     * @param user
     */
    update(user: User): Observable<any> {
        return this._httpClient.patch<User>('api/common/user', { user }).pipe(
            map((response) => {
                this._user.next(response);
            })
        );
    }

    changePassword(
        userId: string,
        params: {
            currentPassword: string;
            newPassword: string;
            confirmNewPassword: string;
        }
    ): Observable<User> {

        const userService = this.getServiceEndpoint();

        const header = {
            headers: new HttpHeaders().set(
                'Authorization',
                this._authService.accessToken
            ),
        };

        return this._httpClient
            .put<ApiResponse<User>>(
                `${userService}/users/${userId}/change-password`,
                params,
                header
            )
            .pipe(
                map((response) => response.data)
            );
    }

    editUser(userId: string, user: User): Observable<any> {

        let userService = this.getServiceEndpoint();

        const header = {
            headers: new HttpHeaders().set(
                'Authorization',
                this._authService.accessToken
            ),
        };

        return this._httpClient.put<any>(
            `${userService}/users/${userId}/change-profile`,
            user,
            header
        ).pipe(
            tap((response) => {
                this._logging.debug(
                    'Response from UserService (editUser)',
                    response
                );
            })
        );
    }

    getAllUsersAdmin(
        params: {
            page: number;
            pageSize: number;
            sortBy: string;
            sortingOrder: string;
            roles: string[];
            status?: string;
        } = {
                page: null,
                pageSize: null,
                sortBy: null,
                sortingOrder: null,
                roles: null,
                status: null,
            }
    ): Observable<any> {
        let userService = this.getServiceEndpoint();
        const header = {
            headers: new HttpHeaders().set(
                'Authorization',
                this._authService.accessToken
            ),
            params,
        };

        return this._httpClient
            .get<any>(userService + '/admins/users/pagination', header)
            .pipe(
                catchError(() =>
                    // Return false
                    of(false)
                ),
                switchMap(async (response: any) => {
                    this._logging.debug(
                        'Response from UserService (getAllAdminUser)',
                        response
                    );

                    const data = response?.data;

                    this._users.next(data?.content ?? []);

                    const usersPagination: UsersPagination = {
                        length: data?.totalElements ?? 0,
                        size: data?.size ?? 0,
                        page: data?.number ?? 0,
                        lastPage: data?.totalPages ?? 0,
                        startIndex: data?.pageable?.offset ?? 0,
                        endIndex:
                            (data?.pageable?.offset ?? 0) +
                            (data?.numberOfElements ?? 0) - 1,
                    };

                    this._usersPagination.next(usersPagination);
                })
            );
    }

    registerUser(user: User) {
        let userService = this.getServiceEndpoint();
        const header = {
            headers: new HttpHeaders().set(
                'Authorization',
                this._authService.accessToken
            ),
        };

        return this.users$.pipe(
            take(1),
            switchMap((users) =>
                this._httpClient
                    .post<any>(userService + '/auth/register', user, header)
                    .pipe(
                        map((response) => {
                            this._logging.debug(
                                'Response from UserService (registerUser)',
                                response
                            );

                            // Update the user
                            users.unshift(response.data);

                            // Update the products
                            this._users.next(users);

                            return response.data;
                        })
                    )
            )
        );
    }

    makeAsAdmin(user: User): Observable<any> {
        let userService = this.getServiceEndpoint();
        const header = {
            headers: new HttpHeaders().set(
                'Authorization',
                this._authService.accessToken
            ),
        };

        return this.users$.pipe(
            take(1),
            switchMap((users) =>
                this._httpClient
                    .post<any>(userService + '/admins/assign-as-admin', user, header)
                    .pipe(
                        map((response) => {
                            this._logging.debug(
                                'Response from UserService (makeAsAdmin)',
                                response
                            );

                            let index = users.findIndex(
                                (user) => user.id === response.data.id
                            );
                            if (index > -1) {
                                // Update the user in its current position
                                users[index] = response.data;
                            }

                            // Update the products
                            this._users.next(users);

                            return response.data;
                        })
                    )
            )
        );
    }


    getAllUsers(
        params: {
            from?: string | null;
            to?: string | null;
            globalSearch?: string | null;
            page?: number | null;
            pageSize?: number | null;
            sortBy?: string | null;
            sortingOrder?: string | null;
            status?: string | null;
            roles?: string[] | null;
        } = {
                from: null,
                to: null,
                globalSearch: null,
                page: null,
                pageSize: null,
                sortBy: null,
                sortingOrder: null,
                status: null,
                roles: null,
            }
    ): Observable<any> {
        let reportService = this.getServiceEndpoint();

        const cleanedParams: any = {};

        Object.entries(params).forEach(([key, value]) => {
            if (
                value !== null &&
                value !== undefined &&
                value !== '' &&
                !(Array.isArray(value) && value.length === 0)
            ) {
                cleanedParams[key] = value;
            }
        });

        const header = { params: cleanedParams };

        return this._httpClient
            .get<any>(reportService + '/users/pagination', header)
            .pipe(
                catchError((error) => {
                    console.error('An error occurred:', error);
                    return throwError(error); // Propagate the error
                }),
                map((response) => {

                    this._logging.debug(
                        'Response from User Service (getAllUsers)',
                        response
                    );

                    const data = response?.data;

                    this._users.next(data?.content ?? []);

                    const userPagination = {
                        length: data?.totalElements ?? 0,
                        size: data?.size ?? 0,
                        page: data?.number ?? 0,
                        lastPage: data?.totalPages ?? 0,
                        startIndex: data?.pageable?.offset ?? 0,
                        endIndex:
                            (data?.pageable?.offset ?? 0) +
                            (data?.numberOfElements ?? 0) - 1,
                    };

                    this._usersPagination.next(userPagination);

                    return response;
                })
            );
    }

    updateUserStatus(id: string, status: 'ACTIVE' | 'INACTIVE'): Observable<any> {
        let userService = this.getServiceEndpoint();
        const header = {
            headers: new HttpHeaders().set(
                'Authorization',
                this._authService.accessToken
            ),
            params: { status }
        };

        return this.users$.pipe(
            take(1),
            switchMap((users) =>
                this._httpClient
                    .put<any>(userService + '/users/' + id + '/change-status', null, header)
                    .pipe(
                        map((response) => {
                            this._logging.debug(
                                'Response from UserService (updateUserStatus)',
                                response
                            );

                            let index = users.findIndex(
                                (user) => user.id === response.data.id
                            );
                            if (index > -1) {
                                // Update the user in its current position
                                users[index] = response.data;
                            }

                            // Update the products
                            this._users.next(users);

                            return response.data;
                        })
                    )
            )
        );
    }

    topupWallet(body: { userId: string; amount: number }): Observable<any> {
        const userService = this.getServiceEndpoint();

        const header = {
            headers: new HttpHeaders().set(
                'Authorization',
                this._authService.accessToken
            ),
        };

        return this._httpClient
            .post<any>(userService + '/wallet/topup', body, header)
            .pipe(
                tap((response) => {
                    this._logging.debug(
                        'Response from UserService (topupWallet)',
                        response
                    );
                })
            );
    }
}
